import { useEffect, useState } from "react";
import { api, type NotificationSettings } from "./api.ts";
import { useLoad } from "./hooks.ts";
import {
  canPush,
  type DeviceState,
  deviceState,
  deviceWord,
  pushSubscription,
  renewSubscription,
  subscribe,
} from "./push.ts";
import { ReportThis } from "./ReportThis.tsx";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { SwitchRow } from "./Switch.tsx";
import { Button, Icon } from "./souvenir.ts";

// Notifications (features/notifications.md): offered once on Your trips, and on the profile in two plainly named
// parts (DESIGN.md, "Notifications"): "On this phone", one switch for this device, and "What to tell you about", every
// kind a switch saying where it arrives (the admin's alerts too, for its owners, and the weekly round-up and Offers
// from Tripmo, by email only). A kind off means none of it arrives, by push or by email.

type Kind = keyof NotificationSettings["kinds"];

// Every kind, as the profile lists it, and where each arrives: pushed to the devices that are on, and for some
// emailed too (`email`), or emailed for one part of it (`emailFor`: Waiting on you's trip offered to you); the weekly
// round-up only by email (`emailOnly`, its words). Reminders off says plainly what
// stops (`whenOff`: features/notifications.md, "Reminders off, said plainly").
interface KindRow {
  kind: Kind;
  label: string;
  about: string;
  email?: true;
  emailFor?: string;
  emailOnly?: string;
  whenOff?: string;
}
const KINDS: KindRow[] = [
  {
    kind: "reminders",
    label: "Reminders",
    about:
      "When booking opens, when free cancellation is about to end, when a card on file is charged soon, and reminders your AI sets.",
    email: true,
    whenOff: "You won't hear when booking opens, free cancellation ends or a card is charged.",
  },
  {
    kind: "due",
    label: "Due dates",
    about:
      "Your to-dos, and those that are nobody's yet: 3 days before they're due, on the day, and the day after if still open. At 9:00 your time.",
    email: true,
  },
  {
    kind: "waiting",
    label: "Waiting on you",
    about: "Questions for you, people asking to join your trips, and trips offered to you.",
    emailFor: "a trip offered to you",
  },
  { kind: "answers", label: "Answers", about: "When someone answers your question." },
  {
    kind: "changes",
    label: "Changes during the trip",
    about: "When something you're going to changes its time or place, or is cancelled, while the trip is on.",
  },
  {
    kind: "todos",
    label: "To-dos",
    about:
      "When someone gives you a to-do or changes one of yours, and when anything important is done, moved or removed.",
  },
  {
    kind: "ratings",
    label: "Ratings and notes",
    about: "When someone rates places or leaves notes: one notification once they pause.",
  },
  {
    kind: "round_up",
    label: "Weekly round-up",
    about: "For each trip until it ends: what's due soon, still to book, and waiting on your answer or rating.",
    emailOnly: "Email, on Mondays",
  },
  // An offer for the account now and then, such as the sign-up gift's pass (features/notifications.md, "Offers from
  // Tripmo"): each such mail's link turns it off without signing in.
  {
    kind: "offers",
    label: "Offers from Tripmo",
    about: "Now and then, an offer for your account, like a free trip.",
    emailOnly: "Email",
  },
  // Only for the admin's owners: the server sends it to no one else.
  {
    kind: "alerts",
    label: "The admin's alerts",
    about: "When something on Tripmo needs you as its owner, and when it's cleared.",
    email: true,
  },
];

// Where a kind arrives, in words; null where it can't arrive at all (a pushed kind on a server that can't push).
function whereItArrives(k: KindRow, canPush: boolean): string | null {
  if (k.emailOnly) return k.emailOnly;
  if (!canPush) return k.email ? "Email" : k.emailFor ? `Email for ${k.emailFor}` : null;
  return k.email ? "Push and email" : k.emailFor ? `Push, and email for ${k.emailFor}` : "Push";
}

// This device's state, read when shown, and turning it on or off.
function useDevice(settings: NotificationSettings | null) {
  const [state, setState] = useState<DeviceState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    deviceState().then(setState, () => setState("unsupported"));
  }, []);
  const turnOn = async () => {
    if (!settings?.key) return;
    setBusy(true);
    setError(null);
    try {
      const result = await subscribe(settings.key);
      if (typeof result === "string") setState(result);
      else {
        await api.addDevice(result.subscription.toJSON(), result.replaces);
        setState("on");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't turn notifications on.");
    } finally {
      setBusy(false);
    }
  };
  const turnOff = async () => {
    setBusy(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await api.removeDevice(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setState("off");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't turn notifications off.");
    } finally {
      setBusy(false);
    }
  };
  return { state, busy, error, turnOn, turnOff };
}

const DISMISSED = "tripmo:notify-hint";

// On Your trips, once: a device that can get notifications and hasn't been asked. Dismissed, it stays
// gone on that device. `show` is null until it's known (the settings and this device's state are read
// first), so Your trips knows when its one suggestion card is settled.
export interface NotifyHintState {
  show: boolean | null;
  device: ReturnType<typeof useDevice>;
  dismiss: () => void;
}

export function useNotifyHint(): NotifyHintState {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === "dismissed";
    } catch {
      return false;
    }
  });
  const asked = !canPush() || Notification.permission !== "default";
  const settings = useLoad(() => (dismissed || asked ? Promise.resolve(null) : api.notifications()), []);
  const ready = settings.state === "ready" ? settings.data : null;
  const device = useDevice(ready);
  const show = (() => {
    if (dismissed) return false;
    if (settings.state === "loading") return null;
    if (!ready?.available) return false;
    if (device.state === null) return null;
    // Turned on here, or asked already: nothing left to suggest.
    return device.state !== "on" && !(asked && device.state !== "blocked");
  })();
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, "dismissed");
    } catch {}
  };
  return { show, device, dismiss };
}

export function NotifyHint({ hint: { show, device, dismiss } }: { hint: NotifyHintState }) {
  if (!show) return null;
  return (
    <aside className="tm-install" aria-label={`Get reminders on this ${deviceWord()}`}>
      <div className="tm-install-text">
        <h2 className="title">Get reminders on this {deviceWord()}</h2>
        <p className="callout tm-muted">
          When booking opens, when to-dos come due, when free cancellation is about to end, reminders your AI sets, and
          questions waiting on you, even with Tripmo closed.
        </p>
        {device.state === "blocked" ? (
          <p className="callout tm-install-how">
            Notifications are blocked for Tripmo in this browser's settings. Allow them there, then switch on “On this{" "}
            {deviceWord()}” on your profile.
          </p>
        ) : (
          <Button size="sm" icon="bell" className="tm-start" disabled={device.busy} onClick={device.turnOn}>
            Turn on
          </Button>
        )}
        {device.error && (
          <p className="callout" role="alert">
            {device.error}
            <ReportThis />
          </p>
        )}
      </div>
      <button type="button" className="tm-install-close" aria-label="Not now" onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </aside>
  );
}

// On the profile, two parts: this device on or off, then what to tell you about (the account's, on every device).
export function NotificationsSection() {
  const settings = useLoad(api.notifications, []);
  const ready = settings.state === "ready" ? settings.data : null;
  const device = useDevice(ready);
  const [kinds, setKinds] = useState<NotificationSettings["kinds"] | null>(null);
  const shown = kinds ?? ready?.kinds;
  const word = deviceWord();
  const toggle = async (kind: Kind, on: boolean) => {
    if (!shown) return;
    setKinds({ ...shown, [kind]: on });
    try {
      setKinds((await api.setKind(kind, on)).kinds);
    } catch {
      setKinds({ ...shown, [kind]: !on });
    }
  };
  // Why this device can't be turned on, where it can't; null where it can.
  const why = !ready ? null : !ready.available ? (
    "Notifications to devices aren't set up here yet."
  ) : device.state === "needs-install" ? (
    <>
      To get notifications on this iPhone, add Tripmo to your Home Screen first: tap Share, then{" "}
      <span className="tm-nowrap">Add to Home Screen</span>, and open it from there.
    </>
  ) : device.state === "unsupported" ? (
    "This browser can't show notifications from Tripmo."
  ) : device.state === "blocked" ? (
    "Notifications are blocked for Tripmo in this browser's settings. Allow them there to turn this on."
  ) : null;
  const on = device.state === "on";
  // The parts, hairlines between them: this device's switch (or why it can't be on), then every kind.
  const thisDevice =
    settings.state === "failed" ? (
      <p className="callout tm-muted">{settings.message}</p>
    ) : !ready || (ready.available && device.state === null) ? null : (
      <>
        <SwitchRow
          label={`On this ${word}`}
          about={why ?? `Tripmo sends notifications to this ${word}. Turn it on on each phone or computer you use.`}
          checked={on}
          disabled={why !== null}
          busy={device.busy}
          onChange={(v) => {
            if (device.busy) return;
            if (v) void device.turnOn();
            else void device.turnOff();
          }}
        />
        {on && <SendTest word={word} />}
      </>
    );
  const rows = shown
    ? KINDS.filter((k) => shown[k.kind] !== undefined).flatMap((k) => {
        const where = whereItArrives(k, ready?.available === true);
        return where ? [{ ...k, where }] : [];
      })
    : [];
  return (
    <SettingsGroup id="notifications-title" title="Notifications">
      {(thisDevice || device.error) && (
        <div className="tm-settings-part">
          {thisDevice}
          {device.error && (
            <p className="callout" role="alert">
              {device.error}
              <ReportThis />
            </p>
          )}
        </div>
      )}
      {shown && rows.length > 0 && (
        <div className="tm-settings-part">
          <fieldset className="tm-notify-kinds" aria-describedby="notify-kinds-about">
            <legend className="tm-notify-legend">
              <span className="tm-settings-label">What to tell you about</span>
            </legend>
            <p id="notify-kinds-about" className="caption tm-muted tm-notify-kinds-about">
              {ready?.available
                ? "The same on every device. Push goes to each phone or computer that's on. Off means it doesn't come by push or email."
                : "The same on every device. Off means it doesn't come by email."}
            </p>
            {rows.map((k) => (
              <SwitchRow
                key={k.kind}
                label={k.label}
                about={k.about}
                where={k.where}
                whenOff={k.whenOff}
                checked={shown[k.kind] === true}
                onChange={(v) => toggle(k.kind, v)}
              />
            ))}
          </fieldset>
        </div>
      )}
    </SettingsGroup>
  );
}

// Checking notifications reach this device: one goes to it alone, the way every notification goes.
function SendTest({ word }: { word: "phone" | "computer" }) {
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setState("sending");
    setError(null);
    try {
      const subscription = await pushSubscription();
      if (!subscription) throw new Error(`Notifications are off on this ${word}. Turn them on first.`);
      await api.testDevice(subscription.toJSON(), word);
      setState("sent");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't send a test.");
      setState("idle");
    }
  };
  return (
    <div className="tm-notify-test">
      <Button size="sm" icon="send" disabled={state === "sending"} onClick={send}>
        {state === "sending" ? "Sending…" : "Send a test"}
      </Button>
      {state === "sent" && (
        <p className="caption tm-muted" role="status">
          Sent. It should arrive in a few seconds. Nothing after a minute? Check this {word}'s Focus and its
          notification settings for Tripmo.
        </p>
      )}
      {error && (
        <p className="callout" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}

// Each time Tripmo opens signed in: a device with notifications on tells Tripmo again, so one it forgot
// (or a computer now signed in as someone else) is put right. And when Tripmo's key pair was replaced
// since the device subscribed, it subscribes again with the new key, on opening and on coming back into
// view, and Tripmo keeps the new subscription in place of the old one.
export function useKeepDevice(signedIn: boolean) {
  useEffect(() => {
    if (!signedIn || !canPush() || Notification.permission !== "granted") return;
    api.keepDevice(thisDevice(true));
    const back = () => {
      if (document.visibilityState === "visible") api.keepDevice(thisDevice(false));
    };
    document.addEventListener("visibilitychange", back);
    return () => document.removeEventListener("visibilitychange", back);
  }, [signedIn]);
}

// This device's subscription, to tell Tripmo: on opening, always (when notifications are on here);
// otherwise only when it was made again with a new key.
async function thisDevice(opening: boolean) {
  const key = await api.pushKey().catch(() => null);
  const renewed = key ? await renewSubscription(key) : null;
  if (!renewed?.replaces && !opening) return null;
  const subscription = renewed?.subscription ?? (await pushSubscription());
  return subscription && { subscription: subscription.toJSON(), replaces: renewed?.replaces ?? null };
}
